import * as Yup from 'yup'

import React, { useState } from 'react'

import { Link } from 'react-router-dom'
import { Spinner } from '../../../framework/widgets'
import clsx from 'clsx'
import { toAbsoluteUrl } from '../../../helpers'
import { useFormik } from 'formik'
import { useRequestPasswordMutation } from '../../../framework/services/auth'

const initialValues = {
  email: '',
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
})

export function ForgotPassword() {
  const [loading, setLoading] = useState(false)
  const [requestPassword] = useRequestPasswordMutation()
  const [linkSent, setLinkSent] = useState<boolean>(false)

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setStatus('')
      setLoading(true)
      requestPassword(values.email)
        .unwrap()
        .then(() => setLinkSent(true))
        .catch((err) => setStatus(err.data.message || 'The reset password link could not be generated.'))
        .finally(() => {
          setLoading(false)
          setSubmitting(false)
        })
    },
  })

  return (
    <div className='container d-flex justify-content-center align-items-center mt-20'>
      <div className='mw-450px'>
        <div className='card bg-light border'>
          <div className='card-header flex-row'>
            <div className='mt-5 flex-fill d-flex justify-content-center align-items-center'>
              <img
                alt='Logo'
                className='h-50px logo'
                src={toAbsoluteUrl('/media/logos/logo-1.png')}
              />
            </div>
          </div>
          <div className='card-body'>

            <form
              className='form w-350px mx-auto fv-plugins-bootstrap5 fv-plugins-framework'
              noValidate
              id='kt_login_password_reset_form'
              onSubmit={formik.handleSubmit}
            >
              <div className='text-center mb-10'>
                <h1 className='text-dark mb-3'>Forgot Password ?</h1>
                <div className='text-gray-400 fw-semibold fs-7'>
                  Enter your email to reset your password.
                </div>
              </div>

              {formik.status && (
                <div className='alert alert-danger'>
                  <div className='alert-text font-weight-bold'>{formik.status}</div>
                </div>
              )}

              {linkSent && (
                <div className='mb-10 bg-light-info p-8 rounded'>
                  <div className='text-info'>Password reset link sent. Please check your email</div>
                </div>
              )}

              {!linkSent && <>
                <div className='fv-row mb-10'>
                  <label className='form-label fw-bolder text-dark'>Email</label>
                  <input
                    type='email'
                    placeholder=''
                    autoComplete='off'
                    {...formik.getFieldProps('email')}
                    className={clsx(
                      'form-control',
                      { 'is-invalid': formik.touched.email && formik.errors.email },
                      { 'is-valid': formik.touched.email && !formik.errors.email, }
                    )}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.email}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
                  <button
                    type='submit'
                    disabled={loading}
                    id='kt_password_reset_submit'
                    className='btn btn-lg btn-primary fw-bolder me-4'
                  >
                    {loading && <Spinner className='me-2' />}
                    <span className='indicator-label'>Submit</span>
                  </button>

                  <Link to='/auth/login' className='btn btn-lg btn-light-primary fw-bolder'>
                    Cancel
                  </Link>
                </div>
              </>}
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
